import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box,
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

/**
 * SubscriptionSettingsDialog component for subscription options
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to close the dialog
 * @param {boolean} props.subscription90Days - Whether 90 days subscription is selected
 * @param {boolean} props.subscription180Days - Whether 180 days subscription is selected
 * @param {boolean} props.subscription12Month - Whether 12-month contract is selected
 * @param {boolean} props.subscriptionSplitPayment - Whether split payment is selected
 * @param {string} props.region - Current region
 * @param {number} props.monthlyTotal - Monthly total amount
 * @param {Function} props.onApply - Function to apply settings
 */
const SubscriptionSettingsDialog = ({ 
  open, 
  onClose,
  subscription90Days = false,
  subscription180Days = false,
  subscription12Month = false,
  subscriptionSplitPayment = false,
  customDiscount360 = 20,
  customDiscount180 = 0,
  region = 'eu',
  monthlyTotal = 0,
  onApply
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  // Local state for settings
  const [localSubscription90Days, setLocalSubscription90Days] = useState(subscription90Days);
  const [localSubscription180Days, setLocalSubscription180Days] = useState(subscription180Days);
  const [localSubscription12Month, setLocalSubscription12Month] = useState(subscription12Month);
  const [localSubscriptionSplitPayment, setLocalSubscriptionSplitPayment] = useState(subscriptionSplitPayment);
  
  // Check if custom values are non-default (show advanced mode automatically)
  const hasCustomDiscounts = customDiscount360 !== 20 || customDiscount180 !== 0;

  // Advanced mode state
  const [isAdvancedMode, setIsAdvancedMode] = useState(hasCustomDiscounts);
  const [localDiscount360, setLocalDiscount360] = useState(customDiscount360);
  const [localDiscount180, setLocalDiscount180] = useState(customDiscount180);
  
  // String values for inputs (to allow empty state during editing)
  const [discount360String, setDiscount360String] = useState(String(customDiscount360));
  const [discount180String, setDiscount180String] = useState(String(customDiscount180));
  
  // Reset local state when dialog opens
  React.useEffect(() => {
    if (open) {
      setLocalSubscription90Days(subscription90Days);
      setLocalSubscription180Days(subscription180Days);
      setLocalSubscription12Month(subscription12Month);
      setLocalSubscriptionSplitPayment(subscriptionSplitPayment);
      setLocalDiscount360(customDiscount360);
      setLocalDiscount180(customDiscount180);
      setDiscount360String(String(customDiscount360));
      setDiscount180String(String(customDiscount180));
      // Show advanced mode if custom discounts are non-default
      const hasCustomDiscounts = customDiscount360 !== 20 || customDiscount180 !== 0;
      setIsAdvancedMode(hasCustomDiscounts);
    }
  }, [open, subscription90Days, subscription180Days, subscription12Month, subscriptionSplitPayment, customDiscount360, customDiscount180]);

  // Handle keyboard shortcuts for advanced mode
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl+Shift+D (PC) or Cmd+Shift+D (Mac)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        setIsAdvancedMode(prev => !prev);
      }
    };
    
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }
    
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);
  
  // Handle double click on title for advanced mode
  const handleTitleDoubleClick = () => {
    setIsAdvancedMode(prev => !prev);
  };

  // Handle discount input changes
  const handleDiscount360Change = (e) => {
    setDiscount360String(e.target.value);
  };

  const handleDiscount180Change = (e) => {
    setDiscount180String(e.target.value);
  };

  // Handle blur events - convert to number and validate
  const handleDiscount360Blur = () => {
    const numValue = Number(discount360String);
    if (isNaN(numValue) || numValue < 0) {
      const defaultValue = 20;
      setLocalDiscount360(defaultValue);
      setDiscount360String(String(defaultValue));
    } else if (numValue > 50) {
      setLocalDiscount360(50);
      setDiscount360String('50');
    } else {
      setLocalDiscount360(numValue);
      setDiscount360String(String(numValue));
    }
  };

  const handleDiscount180Blur = () => {
    const numValue = Number(discount180String);
    if (isNaN(numValue) || numValue < 0) {
      const defaultValue = 10;
      setLocalDiscount180(defaultValue);
      setDiscount180String(String(defaultValue));
    } else if (numValue > 50) {
      setLocalDiscount180(50);
      setDiscount180String('50');
    } else {
      setLocalDiscount180(numValue);
      setDiscount180String(String(numValue));
    }
  };

  // Handle apply button click
  const handleApply = () => {
    const settings = {
      subscription90Days: localSubscription90Days,
      subscription180Days: localSubscription180Days,
      subscription12Month: localSubscription12Month,
      subscriptionSplitPayment: localSubscriptionSplitPayment,
      customDiscount360: localDiscount360,
      customDiscount180: localDiscount180,
      hasGlobalSettings: localSubscription90Days || localSubscription180Days || localSubscription12Month || localSubscriptionSplitPayment
    };
    if (onApply) {
      onApply(settings);
    } else {
      console.error('onApply function is missing!');
    }
    onClose();
  };
  
  // Check if 12-month contract should be visible
  const show12MonthOption = monthlyTotal >= 250;
  
  // Показувати опцію Split Payment тільки для регіону Україна
  // Використовуємо різні варіанти перевірки для надійності
  const isUkraineRegion = region === 'ua' || 
                       String(region).toLowerCase().includes('ukr') || 
                       String(region).toLowerCase().includes('ua') || 
                       String(region).toLowerCase() === 'ukraine';
  const showSplitPaymentOption = isUkraineRegion;
  

  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: theme.spacing(1),
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: '#f9f9f9',
          py: 1.5,
          '& .MuiTypography-root': {
            fontWeight: 500,
            fontSize: 18,
            cursor: 'pointer',
            userSelect: 'none'
          }
        }}
        onDoubleClick={handleTitleDoubleClick}
      >
        {t('calculator.subscriptionOptions', 'Subscription Options')}
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 3 }}>
          {/* Advanced Discount Settings */}
          {isAdvancedMode && (
            <Box sx={{ 
              mb: 3,
              p: 2, 
              bgcolor: 'rgba(255, 152, 0, 0.08)', 
              borderRadius: 1,
              border: '1px dashed rgba(255, 152, 0, 0.3)'
            }}>
              <Typography variant="caption" sx={{ 
                color: 'orange.main', 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 2,
                display: 'block'
              }}>
                🔒 {t('calculator.advancedDiscountSettings', 'Advanced Discount Settings')}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <TextField
                  label={t('calculator.discount360Days', '360-day discount')}
                  type="number"
                  value={discount360String}
                  onChange={handleDiscount360Change}
                  onBlur={handleDiscount360Blur}
                  variant="outlined"
                  InputProps={{ 
                    endAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>%</Typography>
                  }}
                  sx={{ 
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      backgroundColor: 'white',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '14px',
                      '&.Mui-focused': {
                        color: 'primary.main',
                      }
                    }
                  }}
                  inputProps={{ min: 0, max: 50 }}
                />
                
                <TextField
                  label={t('calculator.discount180Days', '180-day discount')}
                  type="number"
                  value={discount180String}
                  onChange={handleDiscount180Change}
                  onBlur={handleDiscount180Blur}
                  variant="outlined"
                  InputProps={{ 
                    endAdornment: <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>%</Typography>
                  }}
                  sx={{ 
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      backgroundColor: 'white',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '14px',
                      '&.Mui-focused': {
                        color: 'primary.main',
                      }
                    }
                  }}
                  inputProps={{ min: 0, max: 50 }}
                />
              </Box>
              
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('calculator.advancedDiscountHint', 'Press Cmd+Shift+D (Mac) or Ctrl+Shift+D (PC) to hide')}
              </Typography>
            </Box>
          )}

          {/* Main subscription options */}
          {show12MonthOption && (
            <FormControlLabel
              control={
                <Checkbox 
                  checked={localSubscription12Month}
                  onChange={(e) => setLocalSubscription12Month(e.target.checked)}
                  sx={{
                    color: '#00C853',
                    '&.Mui-checked': {
                      color: '#00C853',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body1">
                  {t('calculator.subscription12Month', '12-Month Contract')}
                </Typography>
              }
            />
          )}
          
          {showSplitPaymentOption && (
            <FormControlLabel
              control={
                <Checkbox 
                  checked={localSubscriptionSplitPayment}
                  onChange={(e) => setLocalSubscriptionSplitPayment(e.target.checked)}
                  sx={{
                    color: '#00C853',
                    '&.Mui-checked': {
                      color: '#00C853',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body1">
                  {t('calculator.subscriptionSplitPayment', 'Split Payment')}
                </Typography>
              }
            />
          )}
          
          {/* Legacy options in accordion */}
          <Accordion 
            sx={{ 
              mt: 2, 
              '&:before': { display: 'none' },
              boxShadow: 'none',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                bgcolor: '#f9f9f9',
                '&.Mui-expanded': {
                  minHeight: 48,
                },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t('calculator.legacyOptions', 'Legacy Options')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              {isAdvancedMode && (
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={localSubscription90Days}
                      onChange={(e) => setLocalSubscription90Days(e.target.checked)}
                      sx={{
                        color: '#00C853',
                        '&.Mui-checked': {
                          color: '#00C853',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body1">
                      {t('calculator.subscription90Days', '90 Days Subscription')}
                    </Typography>
                  }
                />
              )}
              
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={localSubscription180Days}
                    onChange={(e) => setLocalSubscription180Days(e.target.checked)}
                    sx={{
                      color: '#00C853',
                      '&.Mui-checked': {
                        color: '#00C853',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body1">
                    {t('calculator.subscription180Days', '180 Days Subscription')}
                  </Typography>
                }
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={onClose} 
          sx={{ 
            color: 'text.secondary',
            borderRadius: theme.spacing(1),
            px: 3
          }}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button 
          onClick={handleApply} 
          variant="contained" 
          sx={{ 
            bgcolor: '#00C853',
            '&:hover': {
              bgcolor: '#00A040',
            },
            borderRadius: theme.spacing(1),
            px: 3
          }}
        >
          {t('common.apply', 'Apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionSettingsDialog;
